<script setup>
// Modal "Conectar PostgreSQL" (Fase 6, tela 05a): nome, host, porta, banco, usuário read-only, senha, views
// permitidas, SSL, timeout e máscara de PII. "Testar conexão" valida contra o banco ANTES de salvar (endpoint de
// collection, não grava) e mostra sucesso/erro; na edição, senha em branco mantém a atual.
import { computed, reactive, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';
import PolarisDataSourceAPI from 'dashboard/api/polaris/dataSources';

const props = defineProps({
  source: { type: Object, default: null },
});

const emit = defineEmits(['close', 'saved']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const testing = ref(false);
const testResult = ref(null); // { ok, viewsFound, viewsMissing, error }

const i18nKey = 'POLARIS.DATA_SOURCES.FORM';

const initialState = {
  name: '',
  host: '',
  port: 5432,
  database: '',
  username: '',
  password: '',
  allowedViews: '',
  ssl: false,
  timeoutMs: 2000,
  maskPii: true,
};

const state = reactive({ ...initialState });

const resetFromSource = source => {
  if (!source) return;
  state.name = source.name || '';
  state.host = source.host || '';
  state.port = source.port || 5432;
  state.database = source.database || '';
  state.username = source.username || '';
  state.password = '';
  state.allowedViews = (source.allowed_views || []).join(', ');
  state.ssl = Boolean(source.ssl);
  state.timeoutMs = source.timeout_ms || 2000;
  state.maskPii = source.mask_pii !== false;
};

resetFromSource(props.source);

const isEditing = computed(() => Boolean(props.source?.id));
const saving = computed(
  () => store.getters['polarisDataSources/getUIFlags'].creatingItem
);

const allowedViewsList = computed(() =>
  state.allowedViews
    .split(',')
    .map(view => view.trim())
    .filter(Boolean)
);

const buildPayload = () => ({
  name: state.name,
  kind: 'postgres',
  allowed_views: allowedViewsList.value,
  timeout_ms: Number(state.timeoutMs) || 2000,
  mask_pii: state.maskPii,
  config: {
    host: state.host,
    port: Number(state.port) || 5432,
    database: state.database,
    username: state.username,
    password: state.password,
    ssl: state.ssl,
  },
});

const formValid = computed(
  () =>
    state.name.trim() &&
    state.host.trim() &&
    state.database.trim() &&
    state.username.trim() &&
    (isEditing.value || state.password) &&
    allowedViewsList.value.length > 0
);

const handleTestConnection = async () => {
  testing.value = true;
  testResult.value = null;
  try {
    const { data } = await PolarisDataSourceAPI.testConnection(buildPayload());
    testResult.value = data;
    if (!data.ok) useAlert(t('POLARIS.DATA_SOURCES.TEST.FAILED'));
  } catch (error) {
    testResult.value = { ok: false, error: 'request_failed' };
    useAlert(
      parseAPIErrorResponse(error) || t('POLARIS.DATA_SOURCES.TEST.FAILED')
    );
  } finally {
    testing.value = false;
  }
};

const handleSubmit = async () => {
  if (!formValid.value) return;
  try {
    const payload = buildPayload();
    if (isEditing.value) {
      await store.dispatch('polarisDataSources/update', {
        id: props.source.id,
        ...payload,
      });
    } else {
      await store.dispatch('polarisDataSources/create', payload);
    }
    useAlert(t('POLARIS.DATA_SOURCES.SAVE.SUCCESS_MESSAGE'));
    emit('saved');
    dialogRef.value.close();
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('POLARIS.DATA_SOURCES.SAVE.ERROR_MESSAGE')
    );
  }
};

const handleClose = () => emit('close');
const handleCancel = () => dialogRef.value.close();

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditing ? $t(`${i18nKey}.TITLE_EDIT`) : $t(`${i18nKey}.TITLE_NEW`)
    "
    :description="$t(`${i18nKey}.DESCRIPTION`)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <Input
        v-model="state.name"
        :label="t(`${i18nKey}.NAME.LABEL`)"
        :placeholder="t(`${i18nKey}.NAME.PLACEHOLDER`)"
      />
      <div class="grid grid-cols-3 gap-3">
        <Input
          v-model="state.host"
          class="col-span-2"
          :label="t(`${i18nKey}.HOST.LABEL`)"
          :placeholder="t(`${i18nKey}.HOST.PLACEHOLDER`)"
        />
        <Input
          v-model="state.port"
          type="number"
          :label="t(`${i18nKey}.PORT.LABEL`)"
        />
      </div>
      <Input
        v-model="state.database"
        :label="t(`${i18nKey}.DATABASE.LABEL`)"
        :placeholder="t(`${i18nKey}.DATABASE.PLACEHOLDER`)"
      />
      <div class="grid grid-cols-2 gap-3">
        <Input
          v-model="state.username"
          :label="t(`${i18nKey}.USERNAME.LABEL`)"
          :placeholder="t(`${i18nKey}.USERNAME.PLACEHOLDER`)"
        />
        <Input
          v-model="state.password"
          type="password"
          :label="t(`${i18nKey}.PASSWORD.LABEL`)"
          :placeholder="
            isEditing
              ? t(`${i18nKey}.PASSWORD.KEEP_PLACEHOLDER`)
              : t(`${i18nKey}.PASSWORD.PLACEHOLDER`)
          "
        />
      </div>
      <Input
        v-model="state.allowedViews"
        :label="t(`${i18nKey}.ALLOWED_VIEWS.LABEL`)"
        :placeholder="t(`${i18nKey}.ALLOWED_VIEWS.PLACEHOLDER`)"
        :message="t(`${i18nKey}.ALLOWED_VIEWS.HELP`)"
        message-type="info"
      />
      <div class="grid grid-cols-3 gap-3">
        <Input
          v-model="state.timeoutMs"
          type="number"
          :label="t(`${i18nKey}.TIMEOUT.LABEL`)"
        />
        <label class="flex items-end gap-2 pb-2 text-sm text-n-slate-12">
          <Checkbox v-model="state.ssl" />
          {{ t(`${i18nKey}.SSL.LABEL`) }}
        </label>
        <label class="flex items-end gap-2 pb-2 text-sm text-n-slate-12">
          <Checkbox v-model="state.maskPii" />
          {{ t(`${i18nKey}.MASK_PII.LABEL`) }}
        </label>
      </div>

      <div
        v-if="testResult"
        class="rounded-md px-3 py-2 text-sm"
        :class="
          testResult.ok
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-ruby-3 text-n-ruby-11'
        "
        role="status"
      >
        <template v-if="testResult.ok">
          {{
            t('POLARIS.DATA_SOURCES.TEST.SUCCESS', {
              views: testResult.views_found.join(', '),
            })
          }}
        </template>
        <template v-else>
          {{ t('POLARIS.DATA_SOURCES.TEST.ERROR_MESSAGE') }}
          <span v-if="testResult.views_missing?.length">
            {{
              t('POLARIS.DATA_SOURCES.TEST.MISSING_VIEWS', {
                views: testResult.views_missing.join(', '),
              })
            }}
          </span>
        </template>
      </div>

      <div class="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          :is-loading="testing"
          :disabled="!formValid"
          @click="handleTestConnection"
        >
          {{ t('POLARIS.DATA_SOURCES.TEST.BUTTON') }}
        </Button>
        <div class="flex gap-2">
          <Button type="button" variant="ghost" @click="handleCancel">
            {{ t('POLARIS.DATA_SOURCES.FORM.CANCEL') }}
          </Button>
          <Button type="submit" :is-loading="saving" :disabled="!formValid">
            {{ t('POLARIS.DATA_SOURCES.FORM.SAVE') }}
          </Button>
        </div>
      </div>
    </form>
    <template #footer />
  </Dialog>
</template>
